import Snackbar from '@/components/elements/notifications/Snackbar/Snackbar';
import { config } from '@/config';
import { APP_ROUTES } from '@/constants/pages';
import { persistResolvedLocationForAnonymous } from '@/features/searchV2/components/AutoSuggest/LocationAutoSuggest/LocationAutoSuggest.helpers';
import { continuedSearchContext, trackSearchPerformed } from '@/features/searchV2/helpers/tracking';
import { SearchContext } from '@/features/searchV2/types/tracking';
import { filterEmptyValues, promiseWrapper } from '@/helpers';
import { trackMpEvent } from '@/helpers/analyticsTS';
import { locationSlug } from '@/helpers/urlTs';
import { DEFAULT_MY_LOCATION_KEYWORD } from '@/hooks/search/useSearch.constants';
import { positionSchema, SearchType } from '@/hooks/search/useSearch.helpers';
import { _t } from '@/locale';
import { DealsSearchState, DEALS_SEARCH_ACTION } from '@/reducers/dealsSearchReducer';
import { AppDispatch } from '@/store';
import { ResolvedLocation } from '@/types/api/services/search';
import { UrlSearchState, URL_FILTER_ID_MAP } from '@/types/state/search';
import { toast } from 'react-toastify';
import { dealsService } from '../services';
import { getSearchPerformedTrackingProps } from '../tracking';
import {
  DealsSearchPreviewResponse,
  dealsSearchPreviewResponseSchema,
  dealsSearchResultsResponseSchema,
} from '../types/dealSearch';

function filterOnlyRequiredSearchParams(params) {
  if (locationSlug(params.location) === DEFAULT_MY_LOCATION_KEYWORD) {
    delete params.location;
  }

  if (params.page === 1) {
    delete params.page;
  }

  if (params['lat'] && params['lon'] && positionSchema.safeParse(`${params['lat']}-${params['lon']}`).success) {
    params['position'] = `${params['lat']}-${params['lon']}`;
    delete params['lat'];
    delete params['lon'];
  }

  const fieldsRequiredOnServer = ['q', 'location', 'position', 'prefs', 'bounds', 'orderBy', 'sort', 'page', 'placeId'];

  // delete extra keys
  Object.keys(params)
    .filter((key) => fieldsRequiredOnServer.indexOf(key) === -1)
    .forEach((key) => {
      delete params[key];
    });

  return filterEmptyValues(params);
}

function computePreviewRedirectType(sections: DealsSearchPreviewResponse['sections']): 'deals' | 'sistaminuten' | null {
  const hasDeals = sections.some((s) => (s.type === 'deals' || s.type === 'brand_deals') && s.items.length > 0);
  const hasSmt = sections.some((s) => s.type === 'sistaminuten' && s.items.length > 0);
  const hasOnlyOneTypeOfResults = hasDeals !== hasSmt;
  return hasOnlyOneTypeOfResults ? (hasDeals ? 'deals' : 'sistaminuten') : null;
}

export async function requestAndSetDealsSearchResults(
  {
    type,
    params,
    state,
    resetAllSearchItems,
    bounds,
    searchContext,
  }: {
    type: SearchType;
    params: UrlSearchState & { placeId?: number };
    state: DealsSearchState;
    resetAllSearchItems: boolean;
    bounds?: string | null;
    searchContext?: SearchContext;
  },
  dispatch: AppDispatch,
) {
  let _bounds = (() => {
    /**
     * when updating location bounds are set to null
     * to remove the bounds from the search request
     * and reset it to undefined in search state
     */
    if (bounds === null) {
      return undefined;
    }

    /**
     * otherwise keep searching with the current bounds
     * in new search request if no new bounds are provided
     */
    return bounds ?? state.bounds;
  })();

  const _params = filterOnlyRequiredSearchParams({ ...params, bounds: _bounds });
  const q = params.q;

  dispatch({ type: DEALS_SEARCH_ACTION.SET_BOUNDS, payload: _bounds });

  const isLoadMore = !resetAllSearchItems;

  if (isLoadMore) {
    dispatch({ type: DEALS_SEARCH_ACTION.SET_EXTENDED_FETCHING, payload: true });
  } else {
    dispatch({ type: DEALS_SEARCH_ACTION.SET_FETCHING, payload: true });
  }

  const isPreview = type === 'deals_preview';
  const isBranded = type === 'brand_deals';
  const subType = type === 'deals_sistaminuten' ? 'sistaminuten' : 'deals';
  const _searchContext = searchContext ?? continuedSearchContext(state.searchContext);

  const response = await promiseWrapper(
    isPreview
      ? dealsService.searchPreview({ params: { ..._params, q } })
      : isBranded
      ? dealsService.searchBranded({
          brandSlug: q,
          params: { ...filterEmptyValues({ ..._params, q: undefined }) },
        })
      : dealsService.search({ type: subType, params: { ..._params, q } }),
  );

  function handleErrorResponse() {
    toast(
      ({ closeToast }) => (
        <Snackbar type="danger" label={_t('serverError')} action={<button onClick={closeToast}>OK</button>} />
      ),
      { autoClose: 3000 },
    );
    dispatch({
      type: isLoadMore ? DEALS_SEARCH_ACTION.SET_EXTENDED_FETCHING : DEALS_SEARCH_ACTION.SET_FETCHING,
      payload: false,
    });

    trackMpEvent({
      name: 'error_shown',
      properties: {
        screen_name: 'deals_search_results',
        error_code: response?.error?.response.status ? `http_${response?.error?.response?.status}` : undefined,
        error_type: 'search_error',
      },
    });
    return;
  }

  if (response.error) {
    handleErrorResponse();
    return;
  }

  // Stored before the results, so a preview that redirects to the list hands this context on. `navigation` is only
  // the browser re-running a search, never the context of the list it shows.
  dispatch({
    type: DEALS_SEARCH_ACTION.SET_SEARCH_CONTEXT,
    payload: _searchContext === 'navigation' ? 'free_search' : _searchContext,
  });

  let resolvedLocation: ResolvedLocation = undefined;
  let previewRedirectType: DealsSearchState['previewRedirectType'] = null;

  if (isPreview) {
    const parsed = dealsSearchPreviewResponseSchema.safeParse(response.data);
    if (parsed.success === false) {
      if (config.env == 'development') {
        const summary = parsed.error.issues.map((i) => `${i.path.join('.')}: ${i.message}`).join('\n');
        console.log(summary);
      }

      handleErrorResponse();
      return;
    }
    resolvedLocation = parsed.data.resolvedLocation;
    previewRedirectType = computePreviewRedirectType(parsed.data.sections ?? []);
    dispatch({ type: DEALS_SEARCH_ACTION.SET_PREVIEW_RESULTS, payload: { preview: parsed.data, previewRedirectType } });
  } else {
    const parsed = dealsSearchResultsResponseSchema.safeParse(response.data);
    if (parsed.success === false) {
      if (config.env == 'development') {
        const summary = parsed.error.issues.map((i) => `${i.path.join('.')}: ${i.message}`).join('\n');
        console.log(summary);
      }
      handleErrorResponse();
      return;
    }
    resolvedLocation = parsed.data.resolvedLocation;

    dispatch({
      type: isLoadMore ? DEALS_SEARCH_ACTION.SET_EXTENDED_RESULTS : DEALS_SEARCH_ACTION.SET_LIST_RESULTS,
      payload: parsed.data,
    });
  }

  if (resolvedLocation) {
    persistResolvedLocationForAnonymous(resolvedLocation);
  }

  const _state: DealsSearchState = {
    ...state,
    preview: isPreview ? response.data : null,
    list: isPreview ? null : response.data,
  };

  // The list the preview redirects to sends its own search_performed.
  if (previewRedirectType) return;

  const properties = getSearchPerformedTrackingProps({
    type,
    state: _state,
    urlState: params,
    screenName: 'deals_search_results',
    context: _searchContext,
  });

  trackSearchPerformed(properties);
}

/**
 * Builds the URL query string for a deals search.
 *
 * Filters (prefs) are mapped from internal IDs to url friendly names via
 * URL_FILTER_ID_MAP, mirroring what buildUrlSearchParams does for regular
 * search.
 */
export function buildUrlSearchQuery(
  p: Pick<UrlSearchState, 'q' | 'location' | 'sort' | 'prefs' | 'lat' | 'lon'> & { placeId?: number },
  isIndexable: boolean,
): string {
  const sp = new URLSearchParams();

  if (!isIndexable) {
    if (p.q) sp.set('q', p.q);
    if (p.location) sp.set('location', p.location);
  }

  if (p.prefs) {
    const selected = `${p.prefs}`.split(',').map((id) => ({ id: +id }));
    for (const filter of selected) {
      if (URL_FILTER_ID_MAP[filter.id]) {
        sp.append(URL_FILTER_ID_MAP[filter.id], '1');
      }
    }
  }

  if (p.sort) sp.set('sort', p.sort);
  if (p.lat != null) sp.set('lat', String(p.lat));
  if (p.lon != null) sp.set('lon', String(p.lon));
  if (p.placeId) sp.set('placeId', String(p.placeId));

  return sp.toString();
}

export function buildDealKeywordSuggestionTarget(
  selection: { keyword: string; placeId?: number; hasDeals: boolean; hasSistaminuten: boolean },
  location?: string,
): { pathname: string; search: string } {
  let pathname: string;
  if (selection.hasDeals === selection.hasSistaminuten) {
    pathname = APP_ROUTES.DEALS_SERP_PREVIEW_NO_INDEX;
  } else if (selection.hasSistaminuten) {
    pathname = APP_ROUTES.SISTA_MINUTEN_SERP_NO_INDEX;
  } else if (selection.hasDeals) {
    pathname = APP_ROUTES.DEALS_SERP_NO_INDEX;
  } else {
    pathname = APP_ROUTES.DEALS_SERP_PREVIEW_NO_INDEX;
  }

  const sp = new URLSearchParams();
  sp.set('q', selection.keyword);
  if (location) sp.set('location', location);
  if (selection.placeId) sp.set('placeId', String(selection.placeId));
  return { pathname, search: sp.toString() };
}
